<script setup lang="ts">
import { ref } from 'vue';

/**
 * The mark beside the fuel count: a red jerry can.
 *
 * ## Authored art now, drawn fallback kept
 *
 * The can is `/assets/ui/fuel-can.webp` — a rendered red fuel canister, cut out
 * the way the diamond was (flood-fill from the border, fringe eroded) — by the
 * same request that turned the diamond from six flat facets into a gem: a real
 * can with paint and a cap says "fuel" in a way a two-tone silhouette cannot,
 * and red is the colour a fuel can actually is. It does not break the
 * drawn-not-fetched rule the coin, the radar bezel and the key caps follow; it
 * takes the diamond's escape: **oversample rather than upsample**. The file is
 * 160 px against a largest realistic use of ~16 CSS px at DPR 3 (≈ 48 px), so it
 * is downsampled everywhere — the failure the rule warns about is the other
 * direction.
 *
 * The drawn can below is the **fallback**, exactly as the gem keeps its drawn
 * facets: a blocked or missing file degrades to a mark that still names the
 * unit, never to a figure with no unit beside it. Verified the same way — abort
 * the request and the SVG draws. It is painted the authored can's own red now,
 * so the fallback and the art agree about what colour fuel is.
 *
 * ## Why a jerry can rather than a fuel pump or a gauge needle
 *
 * A pump reads as *buy fuel*, which is the shop's job and not this readout's, and
 * at 16px its hose is the only thing distinguishing it from a cabinet. A gauge
 * needle is a *proportion*, and this mark sits beside a bare count — so a dial
 * next to `17` would imply a fraction that is no longer shown. A can is a
 * **quantity of fuel you hold**, which is exactly what the number says.
 *
 * ## Sized in `em`
 *
 * `CurrencyIcon`'s convention, so the fuel mark and the money marks beside it in
 * the same row take their size from that row's own font rather than from a pixel
 * height somebody has to re-tune when the chrome moves. `vertical-align` puts it
 * on the x-height's middle, which is where a mark beside a numeral looks seated
 * rather than floating. The wrapper span owns the 1em box and is
 * `position: relative`, so a parent styling images broadly (`inset: 0`,
 * `width: 100%`) fills the span rather than the panel — the trap `CurrencyIcon`
 * records having shipped with.
 *
 * ## Colour is the can's, not the count's
 *
 * The count beside it changes colour — it warms when the tank cannot pay for a
 * sortie — and a mark that changed with it would read as two things being wrong
 * instead of one figure being low. A red can beside a red count still reads as
 * one warning, because the can is red always.
 */
withDefaults(
  defineProps<{
    /**
     * Announce it. On by default, because this mark is the only thing naming the
     * unit now that the word *Fuel* has gone from the strip — the same reason
     * `CurrencyIcon` announces itself once `cr` and `💎` left the markup.
     */
    labelled?: boolean;
  }>(),
  { labelled: true },
);

/** A 404 or a blocked asset falls back to the drawn can below, silently. */
const artFailed = ref(false);
</script>

<template>
  <span
    class="can"
    :role="labelled ? 'img' : undefined"
    :aria-label="labelled ? 'Fuel' : undefined"
    :aria-hidden="labelled ? undefined : 'true'"
  >
    <img
      v-if="!artFailed"
      src="/assets/ui/fuel-can.webp"
      alt=""
      aria-hidden="true"
      draggable="false"
      @error="artFailed = true"
    />
    <svg v-else viewBox="0 0 16 16" focusable="false" aria-hidden="true">
      <path
        d="M4.3 5.2V3.9h4.9v1.3"
        fill="none"
        stroke="#a8362a"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <rect x="10.3" y="2.9" width="2.9" height="2.6" rx="0.7" fill="#2b2b2e" />
      <rect x="2.6" y="5.2" width="10.4" height="8.6" rx="1.2" fill="#c8382b" />
      <path
        d="M4.6 12 11 6.9"
        stroke="#5e1712"
        stroke-width="1.8"
        stroke-linecap="round"
      />
    </svg>
  </span>
</template>

<style scoped>
.can {
  display: inline-block;
  width: 1em;
  height: 1em;
  /* Measured the same way the coin's was: puts the body's centre on the
     x-height's middle beside a numeral. */
  vertical-align: -0.14em;
  /* The wrapper owns the box — see the header comment. */
  position: relative;
  flex: none;
}

img,
svg {
  display: block;
  width: 100%;
  height: 100%;
}

img {
  object-fit: contain;
  user-select: none;
}
</style>
